import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { resolveProductImage } from '../utils/resolveImage'
import { mockProducts } from '../data/products'

const onImgError = (e) => { e.target.src = '/images/placeholder.jpg' }

const slugify = (s) => String(s || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '')

const normalizeAvailability = (p) => {
  const isAvailable = /(tomate|cebola|alho)/i.test(p?.nome || '')
  return { ...p, disponivel: isAvailable }
}

export default function Produto() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addItem, cart } = useCart()

  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [relatedProducts, setRelatedProducts] = useState([])

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await fetch(`/api/produtos/${id}`)
        if (res.ok) {
          const data = await res.json()
          // API devolve { success, data: { produto: {...} } }
          const payload = data?.data?.produto || data?.data
          if (data.success && payload) {
            const p = normalizeAvailability(payload)
            setProduct(p)
            fetchRelated(p.categoria, p.id)
          } else {
            throw new Error('not found')
          }
        } else {
          throw new Error('failed')
        }
      } catch (e) {
        const mock = getMockProduct(id)
        if (!mock) {
          setError('Produto nao encontrado')
        } else {
          const p = normalizeAvailability(mock)
          setProduct(p)
          fetchRelated(p.categoria, p.id, true)
        }
      } finally {
        setLoading(false)
      }
    }
    fetchProduct()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchRelated = async (category, currentId, useMock = false) => {
    if (useMock) {
      setRelatedProducts(getMockRelated(category, currentId).map(normalizeAvailability))
      return
    }
    try {
      const res = await fetch(`/api/produtos?categoria=${encodeURIComponent(category)}&limit=4`)
      if (res.ok) {
        const data = await res.json()
        const list = data?.data?.produtos || data?.data || []
        const filtered = list.filter((p) => p.id !== currentId).slice(0, 4)
        setRelatedProducts(filtered.map(normalizeAvailability))
      } else {
        setRelatedProducts(getMockRelated(category, currentId).map(normalizeAvailability))
      }
    } catch {
      setRelatedProducts(getMockRelated(category, currentId).map(normalizeAvailability))
    }
  }

  const handleQuantityChange = (delta) => {
    const next = quantity + delta
    const max = product?.stock || 20
    if (next >= 1 && next <= max) setQuantity(next)
  }

  const handleAddToCart = () => {
    if (!product?.disponivel) return
    for (let i = 0; i < quantity; i++) addItem(product)
  }

  const isInCart = product && cart.some((item) => item.id === product.id)
  const productImages = product?.imagens || [resolveProductImage(product)]
  const hasDiscount = Number.isFinite(product?.precoOriginal) && Number.isFinite(product?.preco) && product.precoOriginal > product.preco
  const discountPercentage = hasDiscount
    ? Math.round(((product.precoOriginal - product.preco) / product.precoOriginal) * 100)
    : 0
  const weightMatch = /(\d+[.,]?\d*)\s*(kg|caixa|saco)/i.exec(product?.nome || '')
  const weightLabel = weightMatch ? `${weightMatch[1].replace(',', '.')} ${weightMatch[2]}` : null
  const totalPrice = Number.isFinite(product?.preco) ? product.preco * quantity : null

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>A carregar produto...</p>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">!</div>
          <h2>Produto nao encontrado</h2>
          <p>O produto que procura nao existe ou foi removido.</p>
          <div className="error-actions">
            <button onClick={() => navigate('/produtos')} className="cta-button primary">Voltar aos produtos</button>
            <button onClick={() => navigate('/')} className="cta-button secondary">Pagina inicial</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="product-detail-page">
      <nav className="breadcrumb">
        <div className="container">
          <Link to="/">Inicio</Link>
          <span> / </span>
          <Link to="/categorias">Categorias</Link>
          <span> / </span>
          <Link to={`/categorias/${slugify(product.categoria)}`}>{product.categoria}</Link>
          <span> / </span>
          <span>{product.nome}</span>
        </div>
      </nav>

      <section className="product-main">
        <div className="container">
          <div className="product-layout">
            <div className="product-gallery">
              <div className="gallery-main">
                <img
                  src={productImages[selectedImage]}
                  alt={product.nome}
                  onError={onImgError}
                  className="main-image"
                />
                {hasDiscount && <div className="discount-badge">-{discountPercentage}%</div>}
                {!product.disponivel && <div className="unavailable-overlay"><span>Indisponivel</span></div>}
              </div>
              {productImages.length > 1 && (
                <div className="gallery-thumbnails">
                  {productImages.map((img, idx) => (
                    <button
                      key={idx}
                      className={`thumbnail ${selectedImage === idx ? 'active' : ''}`}
                      onClick={() => setSelectedImage(idx)}
                    >
                      <img src={img} alt={`${product.nome} ${idx + 1}`} onError={onImgError} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="product-info">
              <div className="product-header">
                <div className="product-badges">
                  <span className="product-category">{product.categoria}</span>
                  {weightLabel && <span className="pill badge-soft">{weightLabel}</span>}
                  {product.disponivel ? (
                    <span className="pill badge-success">Disponivel</span>
                  ) : (
                    <span className="pill badge-muted">Indisponivel</span>
                  )}
                </div>
                <h1 className="product-title">{product.nome}</h1>
                {product.descricao && <p className="product-lead">{product.descricao}</p>}
              </div>

              <div className="product-pricing">
                <div className="current-price">
                  {Number.isFinite(product.preco)
                    ? product.preco.toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })
                    : '--'}
                </div>
                {hasDiscount && Number.isFinite(product.precoOriginal) && (
                  <div className="original-price">
                    {product.precoOriginal.toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })}
                  </div>
                )}
                {weightLabel && <div className="price-note">Peso/embalagem: {weightLabel}</div>}
              </div>

              <div className="product-actions">
                {product.disponivel ? (
                  <>
                    <div className="quantity-and-summary">
                      <div className="quantity-selector">
                        <label>Quantidade:</label>
                        <div className="quantity-controls">
                          <button onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1} className="quantity-btn">-</button>
                          <span className="quantity-display">{quantity}</span>
                          <button onClick={() => handleQuantityChange(1)} disabled={quantity >= (product.stock || 20)} className="quantity-btn">+</button>
                        </div>
                        {product.stock && <span className="stock-info">{product.stock} unidades disponiveis</span>}
                      </div>
                      <div className="order-summary">
                        <div className="summary-row">
                          <span>Subtotal</span>
                          <strong>{totalPrice !== null ? totalPrice.toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' }) : '--'}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="action-buttons">
                      <button
                        onClick={handleAddToCart}
                        className={`cta-button primary large ${isInCart ? 'in-cart' : ''}`}
                      >
                        {isInCart ? 'No carrinho' : 'Adicionar ao carrinho'}
                      </button>
                      <button className="cta-button secondary">Favorito</button>
                    </div>
                  </>
                ) : (
                  <div className="unavailable-message">
                    <div className="unavailable-icon">⌛</div>
                    <div>
                      <h4>Produto indisponivel</h4>
                      <p>Este produto esta temporariamente fora de stock.</p>
                      <button className="cta-button secondary">Avise-me quando disponivel</button>
                    </div>
                  </div>
                )}
              </div>

              {product.caracteristicas && product.caracteristicas.length > 0 && (
                <div className="product-features">
                  <h4>Qualidade garantida</h4>
                  <ul className="features-list">
                    {product.caracteristicas.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="delivery-info">
                <div className="delivery-item">
                  <span className="icon">🚚</span>
                  <div>
                    <strong>Entrega rapida</strong>
                    <p>24-48h em areas abrangidas</p>
                  </div>
                </div>
                <div className="delivery-item">
                  <span className="icon">✅</span>
                  <div>
                    <strong>Produto local</strong>
                    <p>Origem certificada</p>
                  </div>
                </div>
                <div className="delivery-item">
                  <span className="icon">ℹ️</span>
                  <div>
                    <strong>Devolucao facil</strong>
                    <p>7 dias para trocas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="related-products">
          <div className="container">
            <div className="section-header">
              <h2>Produtos relacionados</h2>
              <Link to={`/categorias/${slugify(product.categoria)}`} className="view-all-link">Ver todos</Link>
            </div>
            <div className="products-grid compact">
              {relatedProducts.map((rp) => (
                <div key={rp.id} className="product-card">
                  <div className="product-image">
                    <img src={resolveProductImage(rp)} onError={onImgError} alt={rp.nome} />
                    {!rp.disponivel && <div className="availability-badge unavailable">Indisponivel</div>}
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{rp.nome}</h3>
                    <div className="product-footer">
                      <div className="product-price">
                        {Number.isFinite(rp.preco)
                          ? rp.preco.toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })
                          : '--'}
                      </div>
                      <button
                        className={`add-to-cart-btn ${!rp.disponivel ? 'disabled' : ''}`}
                        onClick={() => navigate(`/produtos/${rp.id}`)}
                      >
                        {rp.disponivel ? 'Ver detalhes' : 'Indisponivel'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

function getMockProduct(productId) {
  const asNumber = Number(productId)
  const asSlug = slugify(productId)
  return (
    mockProducts.find((p) => p.id === asNumber) ||
    mockProducts.find((p) => slugify(p.nome) === asSlug) ||
    null
  )
}

function getMockRelated(category, currentId) {
  return mockProducts
    .filter((p) => slugify(p.categoria) === slugify(category) && p.id !== currentId)
    .slice(0, 4)
}
